import { useLazyGetWorkspaceFiltersQuery } from '@carboncopyco/data';
import { SearchOutlineIcon, SortIcon } from '@carboncopyco/icons';
import type { Sort } from '@carboncopyco/types';
import {
  ColorHex,
  DropdownFilter,
  type DropdownFilterItemContent,
  InputText,
  MultiDropdownFilter,
  renderCreatorItem,
  renderEntityItem,
} from '@carboncopyco/ui';
import { capitalize } from '@carboncopyco/utils';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { sortOptions } from '../../consts';
import { usePlaybookModalState } from '../PlaybookModalContext';
import type { AssetSelectionEntities } from '../types';

const typeOptions: DropdownFilterItemContent<AssetSelectionEntities>[] = [
  { label: 'Demo', id: 'demo' },
  { label: 'Tour', id: 'tour' },
];

export const AssetSelectionControls = () => {
  const { organizationId } = useParams();

  const {
    query,
    setQuery,
    sort,
    setSort,
    creators,
    setCreators,
    types,
    setTypes,
    workspaceId,
  } = usePlaybookModalState();

  const [getWorkspaceFilters, { data: filters }] =
    useLazyGetWorkspaceFiltersQuery();

  useEffect(() => {
    getWorkspaceFilters({
      includeShared: false,
      organizationId: Number(organizationId),
      query,
      workspaceId,
      entities: ['demo', 'tour'],
    });
  }, [organizationId, query, workspaceId, types, getWorkspaceFilters]);

  const creatorOptions =
    filters?.creators?.map((creator) => ({
      id: creator.userId,
      label: capitalize(creator.userName),
      image: creator.image,
    })) || [];

  const debouncedOnChange = useDebouncedCallback(setQuery, 350);

  return (
    <div className="flex items-center justify-between gap-3 px-3 mb-2">
      <DropdownFilter<Sort>
        placeholder=""
        rounded={false}
        capsuleWidth={150}
        selected={sort}
        leftIcon={SortIcon}
        items={sortOptions}
        onSelect={setSort}
      />
      <MultiDropdownFilter
        capsuleWidth={150}
        rounded={false}
        placeholder="Created by"
        fixedGroup={{
          items: creatorOptions,
        }}
        renderItem={renderCreatorItem}
        selectedItems={Object.fromEntries(
          creatorOptions
            .filter((item) => creators[item.id])
            .map((item) => [item.id, item.label])
        )}
        onChange={(creator) =>
          setCreators({
            ...creators,
            [creator]: !creators[creator],
          })
        }
        onClear={() => setCreators({})}
      />

      <MultiDropdownFilter<AssetSelectionEntities>
        capsuleWidth={150}
        rounded={false}
        placeholder="All types"
        fixedGroup={{ items: typeOptions }}
        renderItem={renderEntityItem}
        selectedItems={Object.fromEntries(
          typeOptions
            .filter((item) => types[item.id])
            .map((item) => [item.id, item.label])
        )}
        onChange={(type) => setTypes({ ...types, [type]: !types[type] })}
        onClear={() => setTypes({} as Record<AssetSelectionEntities, boolean>)}
      />

      <InputText
        fullWidth
        placeholder="Search"
        size="md"
        leftElement={<SearchOutlineIcon color={ColorHex.natural300} />}
        onChange={(event) => {
          const value = event.target.value;
          debouncedOnChange(value);
          if (!value) {
            debouncedOnChange.flush();
          }
        }}
      />
    </div>
  );
};
